// ===========================================
// Achievement Definitions Management Page
// ===========================================
// CRUD interface for managing achievement badges and their definitions

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  IconButton,
  Tooltip,
  Skeleton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useAchievementDefinitions,
  useCreateAchievementDefinition,
  useUpdateAchievementDefinition,
  useDeleteAchievementDefinition,
} from '../../hooks/useGamification';
import {
  AchievementDefinition,
  AchievementType,
  CreateAchievementDefinitionData,
  ACHIEVEMENT_ICONS,
} from '../../types/gamification.types';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function AchievementDefinitionsPage() {
  const { data: achievementsData, isLoading, error } = useAchievementDefinitions();
  const createMutation = useCreateAchievementDefinition();
  const updateMutation = useUpdateAchievementDefinition();
  const deleteMutation = useDeleteAchievementDefinition();

  // Get achievements array from response
  const achievements = achievementsData ?? [];

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);
  const [typeFilter, setTypeFilter] = useState<AchievementType | ''>('');

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<AchievementDefinition | null>(null);
  const [formData, setFormData] = useState<CreateAchievementDefinitionData>({
    name: '',
    description: '',
    type: 'ATTENDANCE_MILESTONE',
    threshold: 1,
    badgeIcon: 'star',
    badgeColor: '#FFD700',
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [achievementToDelete, setAchievementToDelete] = useState<AchievementDefinition | null>(null);

  // Filter achievements
  const filteredAchievements = useMemo((): AchievementDefinition[] => {
    let filtered = achievements;

    if (hideInactive) {
      filtered = filtered.filter((achievement) => achievement.isActive);
    }

    if (typeFilter) {
      filtered = filtered.filter((achievement) => achievement.type === typeFilter);
    }

    return filtered;
  }, [achievements, hideInactive, typeFilter]);

  // Handlers
  const handleAdd = () => {
    setEditingAchievement(null);
    setFormData({
      name: '',
      description: '',
      type: 'ATTENDANCE_MILESTONE',
      threshold: 1,
      badgeIcon: 'star',
      badgeColor: '#FFD700',
    });
    setModalOpen(true);
  };

  const handleEdit = (achievement: AchievementDefinition) => {
    setEditingAchievement(achievement);
    setFormData({
      name: achievement.name,
      description: achievement.description,
      type: achievement.type,
      threshold: achievement.threshold,
      badgeIcon: achievement.badgeIcon,
      badgeColor: achievement.badgeColor,
      isActive: achievement.isActive,
    });
    setModalOpen(true);
  };

  const handleDelete = (achievement: AchievementDefinition) => {
    setAchievementToDelete(achievement);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingAchievement) {
        await updateMutation.mutateAsync({
          id: editingAchievement.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (achievementToDelete) {
      try {
        await deleteMutation.mutateAsync(achievementToDelete.id);
        setDeleteDialogOpen(false);
        setAchievementToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  // Get icon emoji from key
  const getIconEmoji = (iconKey: string): string => {
    return ACHIEVEMENT_ICONS[iconKey] || '⭐';
  };

  return (
    <Box>
      <PageHeader
        title="Achievement Definitions"
        subtitle="Manage achievement badges and their criteria. Define what students can earn."
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Achievement Definitions' },
        ]}
        actionLabel="Add Achievement"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load achievement definitions. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AchievementType | '')}
            label="Filter by Type"
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="ATTENDANCE_MILESTONE">Attendance Milestone</MenuItem>
            <MenuItem value="LEVEL_COMPLETION">Level Completion</MenuItem>
            <MenuItem value="STREAK">Streak</MenuItem>
            <MenuItem value="PERFECT_TERM">Perfect Term</MenuItem>
            <MenuItem value="FIRST_RECITAL">First Recital</MenuItem>
            <MenuItem value="EXAM_PASSED">Exam Passed</MenuItem>
            <MenuItem value="PIECE_MASTERED">Piece Mastered</MenuItem>
            <MenuItem value="SPECIAL">Special</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive"
        />

        {filteredAchievements.length !== achievements.length && (
          <Chip
            label={`Showing ${filteredAchievements.length} of ${achievements.length} achievements`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Achievements Table */}
      <TableContainer component={Paper}>
        {isLoading ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Icon/Badge</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Threshold</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton variant="circular" width={40} height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="60%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rectangular" width={60} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={80} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : filteredAchievements.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {achievements.length > 0
                ? 'No achievements match the current filters.'
                : "No achievement definitions configured. Click 'Add Achievement' to create one."}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Icon/Badge</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Threshold</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAchievements.map((achievement) => (
                <TableRow key={achievement.id} hover>
                  <TableCell>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: achievement.badgeColor,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        border: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {getIconEmoji(achievement.badgeIcon)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {achievement.name}
                    </Typography>
                    {achievement.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {achievement.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {achievement.type.replace(/_/g, ' ').toLowerCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{achievement.threshold}</Typography>
                  </TableCell>
                  <TableCell>
                    {achievement.isActive ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Chip label="Inactive" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(achievement)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={achievement.isDefault ? 'Cannot delete default achievements' : 'Delete'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => !achievement.isDefault && handleDelete(achievement)}
                            color={achievement.isDefault ? 'default' : 'error'}
                            disabled={achievement.isDefault}
                            sx={{
                              opacity: achievement.isDefault ? 0.5 : 1,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingAchievement ? 'Edit Achievement' : 'Add Achievement'}
        submitLabel={editingAchievement ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            placeholder="e.g., First Week Complete"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />

          <TextField
            label="Description"
            placeholder="e.g., Attended first week of lessons"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as AchievementType })}
              label="Type"
            >
              <MenuItem value="ATTENDANCE_MILESTONE">Attendance Milestone</MenuItem>
              <MenuItem value="LEVEL_COMPLETION">Level Completion</MenuItem>
              <MenuItem value="STREAK">Streak</MenuItem>
              <MenuItem value="PERFECT_TERM">Perfect Term</MenuItem>
              <MenuItem value="FIRST_RECITAL">First Recital</MenuItem>
              <MenuItem value="EXAM_PASSED">Exam Passed</MenuItem>
              <MenuItem value="PIECE_MASTERED">Piece Mastered</MenuItem>
              <MenuItem value="SPECIAL">Special</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Threshold"
            type="number"
            placeholder="e.g., 10"
            value={formData.threshold}
            onChange={(e) => setFormData({ ...formData, threshold: parseInt(e.target.value) || 1 })}
            required
            fullWidth
            inputProps={{ min: 1 }}
            helperText="Number required to earn this achievement (e.g., lessons attended, weeks streaked)"
          />

          <FormControl fullWidth required>
            <InputLabel>Badge Icon</InputLabel>
            <Select
              value={formData.badgeIcon}
              onChange={(e) => setFormData({ ...formData, badgeIcon: e.target.value })}
              label="Badge Icon"
            >
              {Object.entries(ACHIEVEMENT_ICONS).map(([key, emoji]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{emoji}</Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/-/g, ' ')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Badge Color"
            type="text"
            placeholder="e.g., #FFD700"
            value={formData.badgeColor}
            onChange={(e) => setFormData({ ...formData, badgeColor: e.target.value })}
            required
            fullWidth
            helperText="Hex color code for badge background (e.g., #FFD700 for gold)"
            InputProps={{
              startAdornment: (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: formData.badgeColor,
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    mr: 1,
                  }}
                />
              ),
            }}
          />

          {editingAchievement && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Achievement Definition"
        message={`Are you sure you want to delete "${achievementToDelete?.name}"? Students who earned this achievement will keep their badge, but it can no longer be awarded.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
